/**
 * React Query hook + helpers for Reports > Loyalty.
 *
 *   useLoyalty(filters, enabled)   GET /v1/retail/loyalty
 *   downloadLoyaltyXlsx(filters)   GET /v1/retail/loyalty/export
 *
 * How much of what we sold went to loyalty members, and how many new members we
 * signed up, for the week to date or a chosen fiscal week. The api returns the
 * whole report already narrowed by the filters, so the page renders it as given
 * (sorting is internal to the shared screen).
 *
 * Contract:
 *
 *   GET /v1/retail/loyalty
 *     ?week=YYYY-MM-DD          optional, any date in the fiscal week; absent = week to date
 *     &compare=...              optional, prior_week (default) | last_year | none
 *     &store_no=...             optional, repeatable (a filter, never a scope)
 *   -> { as_of, data_through, scope, period, comparison, weeks,
 *        stores, total, daily }
 *
 * The types come from `@ampersandbrands/ui`, because the shared screen owns the
 * wire shape. The daily email goes through the generic `useReportSubscriptions`
 * hook; what lives here is the pure pair that decides which filters a
 * subscription saves. Same 404/503 "not deployed yet" convention as the other
 * report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type { LoyaltyCompare, LoyaltyFilters, LoyaltyReport } from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const LOYALTY_API_BASE = `${RETAIL_API_BASE}/loyalty`;

export const EMPTY_LOYALTY_FILTERS: LoyaltyFilters = {
  week: '',
  compare: 'prior_week',
  store_no: [],
};

const COMPARES: readonly LoyaltyCompare[] = ['prior_week', 'last_year', 'none'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function asCompare(v: string | null): LoyaltyCompare {
  return COMPARES.includes(v as LoyaltyCompare) ? (v as LoyaltyCompare) : 'prior_week';
}

/** Query string for the report and its export. Lists repeat their key; defaults are left out. */
export function buildLoyaltyQs(filters: LoyaltyFilters): string {
  const sp = new URLSearchParams();
  if (filters.week) sp.set('week', filters.week);
  if (filters.compare !== 'prior_week') sp.set('compare', filters.compare);
  for (const s of filters.store_no) sp.append('store_no', s);
  return sp.toString();
}

/** Read the filters back out of the page's URL. A week that is not a date is ignored (week to date). */
export function parseLoyaltyFilters(sp: URLSearchParams): LoyaltyFilters {
  const week = sp.get('week') ?? '';
  return {
    week: ISO_DAY.test(week) ? week : '',
    compare: asCompare(sp.get('compare')),
    store_no: sp.getAll('store_no').filter(Boolean),
  };
}

/** Write the filters into the page's URL, leaving defaults out. */
export function writeLoyaltyFilters(sp: URLSearchParams, filters: LoyaltyFilters): void {
  sp.delete('week');
  sp.delete('compare');
  sp.delete('store_no');
  // Districts were removed from the report; drop a stale one from an old bookmarked URL.
  sp.delete('district');
  if (filters.week) sp.set('week', filters.week);
  if (filters.compare !== 'prior_week') sp.set('compare', filters.compare);
  for (const s of filters.store_no) sp.append('store_no', s);
}

/**
 * What a subscription saves. The store list is joined into a comma string because
 * the api keeps scalar filter values only. The week is never saved: the email
 * always covers the week to date as of the morning it is sent. The default
 * comparison is left out, since an unset filter is not a filter.
 */
export function loyaltySubscriptionFilters(f: LoyaltyFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.store_no.length > 0) out.store_no = f.store_no.join(',');
  if (f.compare !== 'prior_week') out.compare = f.compare;
  return out;
}

/**
 * Tolerant read, so a payload that is absent or not the report shape degrades to
 * "no data yet" rather than a crash. Returns null unless the parts the screen needs
 * (the period and the fleet total) are there; lists default to empty.
 */
export function normalizeLoyalty(raw: unknown): LoyaltyReport | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const total = r.total as Record<string, unknown> | undefined;
  if (!r.period || typeof r.period !== 'object' || !total || typeof total.current !== 'object' || total.current === null) {
    return null;
  }
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  return {
    as_of: typeof r.as_of === 'string' ? r.as_of : null,
    data_through: typeof r.data_through === 'string' ? r.data_through : null,
    scope: r.scope === 'store' ? 'store' : 'fleet',
    period: r.period as LoyaltyReport['period'],
    comparison: r.comparison && typeof r.comparison === 'object' ? (r.comparison as LoyaltyReport['comparison']) : null,
    weeks: arr(r.weeks),
    stores: arr(r.stores),
    total: {
      current: total.current as LoyaltyReport['total']['current'],
      comparison:
        total.comparison && typeof total.comparison === 'object'
          ? (total.comparison as LoyaltyReport['total']['comparison'])
          : null,
    },
    daily: arr(r.daily),
  };
}

export function useLoyalty(filters: LoyaltyFilters, enabled: boolean) {
  const query = useQuery<LoyaltyReport | null, Error>({
    queryKey: ['retail', 'loyalty', filters],
    queryFn: async () => {
      const qs = buildLoyaltyQs(filters);
      return normalizeLoyalty(await apiGet<unknown>(qs ? `${LOYALTY_API_BASE}?${qs}` : LOYALTY_API_BASE));
    },
    // The mirror refreshes hourly; a few minutes of cache is plenty.
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
    // Keep the old report on screen while a filter change loads, so the toolbar does not flicker.
    placeholderData: (prev) => prev,
  });
  return {
    report: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Browser download of the Excel export, following the filters on screen. */
export async function downloadLoyaltyXlsx(filters: LoyaltyFilters): Promise<void> {
  const qs = buildLoyaltyQs(filters);
  const res = await fetch(`${LOYALTY_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'loyalty.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
